import React, {useState} from 'react';
import {useFormContext, Controller} from 'react-hook-form';
import {AsyncPaginate} from 'react-select-async-paginate';
import http from '../../services/httpService';
import PropTypes from 'prop-types';
import {t, getLanguage} from 'react-switch-lang';
import {Tooltip} from 'antd';
import {useHistory} from 'react-router-dom';

const SelectInfinite = ({
  name,
  defaultValue = null,
  options,
  api,
  defaultOptions = true,
  valueKey = 'id',
  labelKey = 'name',
  descriptionKey = getLanguage() === 'mne' ? 'description' : 'descriptionEn',
  hasTooltipDescription = false,
  formatLabel,
  isRevisionTypes=false  ,
  isSelectWithId=false  ,
  isDepartmentSelect=false  ,
  isExternalization=false  ,
  isKeyIndicator=false  ,
  className,
  selected,
  queryKeys = getLanguage() === 'eng' ? 'nameEn.contains' : 'name.contains',
  isDisabled,
  styles,
  isLink= false,
  onClickLink,
  ...rest
}) => {
  if (!useFormContext())
    throw new Error('Smart components must be in "Form" wrapper component!');
  if (options && api)
    throw new Error(
      'Select component cannot have both "options" and "api" props'
    );
  if (!name) {
    throw new Error('"name" field is required for SelectInfinite component');
  }
  if (!api && !options) {
    throw new Error(
      '"api" or "options" field is required for SelectInfinite component'
    );
  }
  const customStyles = {
    ...styles,
    singleValue: (provided, state) => ({
      ...provided,
      pointerEvents: isLink ? 'auto' : provided?.pointerEvents,
    }),
  };
  const {control, errors} = useFormContext();
  const history = useHistory();
  const getData = async (query, loadedOptions, {page}) => {
    let selectOptions = [];
    let hasMore;
    try {
      if (query) {
        if (api.includes('?')) {
          const {data} = await http.get(`${api}&${queryKeys}=${query}`);
          selectOptions = data;
        } else {
          const {data} = await http.get(`${api}?${queryKeys}=${query}`);
          selectOptions = data;
        }
        // hasMore = selectOptions.length > 0;
      } else {
        if (api.includes('?')) {
          const {data} = await http.get(`${api}&page=${page}`);
          selectOptions = data;
        } else {
          const {data} = await http.get(`${api}?page=${page}`);
          selectOptions = data;
        }
        hasMore = selectOptions.length > 0;
      }
    } catch (err) {}

    const options = selectOptions.map((option) => (
      isRevisionTypes? {
      value: option[valueKey],
      label: option[labelKey] || formatLabel(option),
      abbreviation:option["abbreviation"]
    }:
      isSelectWithId? {
        value: option[valueKey],
        label: option[valueKey] + ' - ' + option[labelKey] || formatLabel(option),
      }:
      isDepartmentSelect ? {
        value: option[valueKey],
        label: option[labelKey] || formatLabel(option),
        sectorDirectorId: option["sectorDirectorId"]
      }:
        isExternalization ? {
        value: option[valueKey],
        label: option[labelKey] || formatLabel(option),
        tin: option["tin"]
      }:
          isKeyIndicator ? {
        value: option[valueKey],
        label: getLanguage() === 'mne' ? option["indicatorName"] : option["indicatorNameEn"],
              escalationCalculationType: option?.escalationCalculationType
      }:
          hasTooltipDescription ? {
            value: option[valueKey],
            label: option[labelKey] || formatLabel(option),
            tooltip: option[descriptionKey]
          }:{
      value: option[valueKey],
      label: option[labelKey] || formatLabel(option),
    }));
    return {options, hasMore, additional: {page: page + 1}};
  };
  return (
    <>
      <Controller
        control={control}
        name={name}
        as={AsyncPaginate}
        loadOptions={!!api && getData}
        loadingMessage={() => t("message.loading")}
        defaultOptions={!!api && defaultOptions}
        defaultValue={selected ? selected : defaultValue}
        options={options}
        additional={{page: 0}}
        className={`${className} ${errors[name] && 'error-field'}`}
        classNamePrefix={'select-infinite'}
        components={{IndicatorSeparator: () => null}}
        isClearable={true}
        isSearchable={true}
        isDisabled={isDisabled}
        debounceTimeout={300}
        noOptionsMessage={() => t('common.noData')}
        styles={customStyles}
        formatOptionLabel={hasTooltipDescription ? (option) => (
          <Tooltip
            title={option.tooltip || option.label}
            overlayClassName="custom-tooltip"
            placement="right"
          >
            <div style={{ cursor: 'pointer' }}>{option.label}</div>
          </Tooltip>
        ) : isLink
          ? (option, { context }) =>
            context === 'value' && isDisabled ? (
              <span
                  style={{ color: '#007dc3', cursor: 'pointer' }}
                  onClick={(e) => {
                      e.stopPropagation();
                      onClickLink(option)
                  }}
                >
            <b>{option.label}</b>
          </span>) : (
              <span><b>{option.label}</b></span>
          )
          : null
      }
        {...rest}
      />
      {errors[name] && (
        <span className="error-message">{errors[name].message}</span>
      )}
    </>
  );
};

export default SelectInfinite;

SelectInfinite.propTypes = {
  name: PropTypes.string.isRequired,
  endpoint: PropTypes.string,
  options: PropTypes.array,
  defaultOptions: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
};
